import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  message,
  Space,
  Tag,
  Popconfirm,
  Tabs,
  List,
  Typography,
  Divider,
  Row,
  Col,
  Tooltip,
  Pagination,
  Empty
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  PhoneOutlined,
  UserOutlined,
  SettingOutlined,
  MessageOutlined,
  TagOutlined
} from '@ant-design/icons';
import axios from 'axios';

// 配置axios基础URL，指向后端服务
axios.defaults.baseURL = (typeof window !== 'undefined' ? window.location.protocol + '//' + window.location.host : '') + '/api';

const { TabPane } = Tabs;
const { TextArea } = Input;
const { Text, Title } = Typography;

const Customers = () => {
  const [customers, setCustomers] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalType, setModalType] = useState('customer'); // 'customer' or 'type'
  const [editingItem, setEditingItem] = useState(null);
  const [followupModalVisible, setFollowupModalVisible] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [followups, setFollowups] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [customerCount, setCustomerCount] = useState(0);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  });
  const [customerForm] = Form.useForm();
  const [typeForm] = Form.useForm();
  const [followupForm] = Form.useForm();

  // 从localStorage获取token，与其他页面保持一致
  const getToken = () => {
    return localStorage.getItem('token') || (localStorage.getItem('auth-storage') ? JSON.parse(localStorage.getItem('auth-storage')).state.token : '');
  };

  // 获取客户列表
  const fetchCustomers = async () => {
    try {
      setLoading(true);
      const token = getToken();
      const params = {
        page: pagination.current,
        limit: pagination.pageSize,
        search: searchText,
        type_id: selectedType || undefined
      };
      console.log('🔍 获取客户列表参数:', params);
      const response = await axios.get(`/customers`, {
        params,
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.data.success) {
        setCustomers(response.data.customers || []);
        if (response.data.pagination) {
          setPagination(prev => ({
            ...prev,
            total: response.data.pagination.total || 0
          }));
        }
      }
    } catch (error) {
      if (error.response?.status !== 401) {
        message.error('获取客户列表失败');
      }
    } finally {
      setLoading(false);
    }
  };

  // 获取客户类型
  const fetchTypes = async () => {
    try {
      const token = getToken();
      const response = await axios.get('/customers/types', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.data.success) {
        setTypes(response.data.types);
      }
    } catch (error) {
      if (error.response?.status !== 401) {
        message.error('获取客户类型失败');
      }
    }
  };

  // 获取跟进记录
  const fetchFollowups = async (customerId) => {
    try {
      const token = getToken();
      const response = await axios.get(`/customers/${customerId}/followups`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.data.success) {
        setFollowups(response.data.followups);
      }
    } catch (error) {
      if (error.response?.status !== 401) {
        message.error('获取跟进记录失败');
      }
    }
  };

  useEffect(() => {
    const token = getToken();
    if (token) {
      fetchCustomers();
      fetchTypes();
    }
  }, [pagination.current, pagination.pageSize, searchText, selectedType]);

  // 处理客户表单提交
  const handleCustomerSubmit = async (values) => {
    try {
      const token = getToken();
      const headers = {
        'Authorization': `Bearer ${token}`
      };
      
      if (editingItem) {
        await axios.put(`/customers/${editingItem.id}`, values, { headers });
        message.success('客户更新成功');
      } else {
        await axios.post('/customers', values, { headers });
        message.success('客户添加成功');
      }
      setModalVisible(false);
      customerForm.resetFields();
      setEditingItem(null);
      fetchCustomers();
    } catch (error) {
      message.error(error.response?.data?.message || '操作失败');
    }
  };

  // 处理类型表单提交
  const handleTypeSubmit = async (values) => {
    try {
      const token = getToken();
      const headers = {
        'Authorization': `Bearer ${token}`
      };
      
      if (editingItem) {
        await axios.put(`/customers/types/${editingItem.id}`, values, { headers });
        message.success('客户类型更新成功');
      } else {
        await axios.post('/customers/types', values, { headers });
        message.success('客户类型添加成功');
      }
      setModalVisible(false);
      typeForm.resetFields();
      setEditingItem(null);
      fetchTypes();
    } catch (error) {
      message.error(error.response?.data?.message || '操作失败');
    }
  };

  // 处理跟进记录提交
  const handleFollowupSubmit = async (values) => {
    try {
      const token = getToken();
      const headers = {
        'Authorization': `Bearer ${token}`
      };
      
      await axios.post(`/customers/${selectedCustomer.id}/followups`, values, { headers });
      message.success('跟进记录添加成功');
      setFollowupModalVisible(false);
      followupForm.resetFields();
      fetchFollowups(selectedCustomer.id);
    } catch (error) {
      message.error(error.response?.data?.message || '添加跟进记录失败');
    }
  };

  // 删除客户
  const handleDeleteCustomer = async (id) => {
    try {
      const token = getToken();
      const headers = {
        'Authorization': `Bearer ${token}`
      };
      
      await axios.delete(`/customers/${id}`, { headers });
      message.success('客户删除成功');
      fetchCustomers();
    } catch (error) {
      message.error(error.response?.data?.message || '删除失败');
    }
  };

  // 删除客户类型
  const handleDeleteType = async (id) => {
    try {
      const token = getToken();
      const headers = {
        'Authorization': `Bearer ${token}`
      };
      
      await axios.delete(`/customers/types/${id}`, { headers });
      message.success('客户类型删除成功');
      fetchTypes();
    } catch (error) {
      message.error(error.response?.data?.message || '删除失败');
    }
  };

  // 打开跟进记录模态框
  const handleViewFollowups = async (customer) => {
    setSelectedCustomer(customer);
    setFollowupModalVisible(true);
    await fetchFollowups(customer.id);
  };

  // 拨打电话
  const handleCall = (phone) => {
    if (phone) {
      window.location.href = `tel:${phone}`;
    }
  };

  // 搜索处理
  const handleSearch = (value) => {
    setSearchText(value);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  // 类型筛选处理
  const handleTypeFilter = (typeId) => {
    console.log('🔍 类型筛选变化:', typeId);
    setSelectedType(typeId);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  // 重置筛选
  const handleResetFilter = () => {
    setSearchText('');
    setSelectedType('');
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  // 分页处理
  const handlePageChange = (page, pageSize) => {
    setPagination(prev => ({ ...prev, current: page, pageSize }));
  };

  // 客户列表列定义
  const customerColumns = [
    {
      title: '客户姓名',
      dataIndex: 'name',
      key: 'name',
      width: 120,
    },
    {
      title: '电话号码',
      dataIndex: 'phone',
      key: 'phone',
      width: 120,
      render: (phone) => (
        <Button
          type="link"
          icon={<PhoneOutlined />}
          onClick={() => handleCall(phone)}
          disabled={!phone}
        >
          {phone || '无'}
        </Button>
      ),
    },
    {
      title: '类型',
      dataIndex: 'type_name',
      key: 'type_name',
      width: 100,
      render: (type_name) => (
        <Tag color="blue">{type_name || '未分类'}</Tag>
      ),
    },
    {
      title: '添加时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 150,
      render: (date) => new Date(date).toLocaleString('zh-CN'),
    },
    {
      title: '备注',
      dataIndex: 'remark',
      key: 'remark',
      ellipsis: true,
    },
    {
      title: '操作',
      key: 'action',
      width: 200,
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            size="small"
            icon={<MessageOutlined />}
            onClick={() => handleViewFollowups(record)}
          >
            跟进记录
          </Button>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => {
              setEditingItem(record);
              setModalType('customer');
              customerForm.setFieldsValue(record);
              setModalVisible(true);
            }}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定要删除这个客户吗？"
            onConfirm={() => handleDeleteCustomer(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button type="link" danger size="small" icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // 客户类型列表列定义
  const typeColumns = [
    {
      title: '类型名称',
      dataIndex: 'type_name',
      key: 'type_name',
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 150,
      render: (date) => new Date(date).toLocaleString('zh-CN'),
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => {
              setEditingItem(record);
              setModalType('type');
              typeForm.setFieldsValue(record);
              setModalVisible(true);
            }}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定要删除这个客户类型吗？"
            onConfirm={() => handleDeleteType(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button type="link" danger size="small" icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="mod-page">
      <div className="mod-header">
        <div>
          <h1 className="mod-header-title">客户管理</h1>
          <div className="mod-header-subtitle">维护客户档案与分类，记录跟进动态，一键拨号联系</div>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => {
            setEditingItem(null);
            setModalType('customer');
            customerForm.resetFields();
            setModalVisible(true);
          }}
        >
          添加客户
        </Button>
      </div>

      <div className="mod-stats">
        <div className="mod-stat-card">
          <div className="mod-stat-icon" style={{ color: '#6366F1' }}><UserOutlined /></div>
          <div>
            <div className="mod-stat-value">{pagination.total}</div>
            <div className="mod-stat-label">客户总数</div>
          </div>
        </div>
        <div className="mod-stat-card">
          <div className="mod-stat-icon" style={{ color: '#8B5CF6' }}><TagOutlined /></div>
          <div>
            <div className="mod-stat-value">{types.length}</div>
            <div className="mod-stat-label">客户类型</div>
          </div>
        </div>
      </div>

      <Card bordered={false} style={{ borderRadius: 14, boxShadow: '0 2px 12px rgba(31,41,80,0.05)' }}>
        <Tabs defaultActiveKey="customers">
          <TabPane tab={`客户列表 (${pagination.total}人)`} key="customers">
            <Card className="mod-toolbar" bodyStyle={{ padding: 16 }} style={{ marginBottom: 16 }}>
              <Row gutter={[12, 12]} align="middle">
                <Col xs={24} sm={12} md={8}>
                  <Input.Search
                    placeholder="搜索客户姓名或电话"
                    style={{ width: '100%' }}
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    onSearch={handleSearch}
                    allowClear
                  />
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <select
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #d9d9d9',
                      borderRadius: '6px',
                      fontSize: '14px',
                      backgroundColor: 'white'
                    }}
                    value={selectedType}
                    onChange={(e) => handleTypeFilter(e.target.value)}
                  >
                    <option value="">所有类型</option>
                    {types.map(type => (
                      <option key={type.id} value={type.id}>
                        {type.type_name}
                      </option>
                    ))}
                  </select>
                </Col>
                <Col xs={24} sm={24} md={10}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    flexWrap: 'wrap',
                    gap: '8px'
                  }}>
                    {(searchText || selectedType) && (
                      <Button
                        onClick={handleResetFilter}
                      >
                        重置筛选
                      </Button>
                    )}
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={() => {
                        setEditingItem(null);
                        setModalType('customer');
                        customerForm.resetFields();
                        setModalVisible(true);
                      }}
                    >
                      添加客户
                    </Button>
                  </div>
                </Col>
              </Row>
            </Card>
            {loading ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <Text>加载中...</Text>
              </div>
            ) : customers.length === 0 ? (
              <Empty
                style={{ padding: '40px 0' }}
                description={<span style={{ color: '#8c8c8c' }}>暂无客户记录，点击“添加客户”开始管理您的客户</span>}
              />
            ) : (
              <Row gutter={[16, 16]}>
                {customers.map((customer) => (
                  <Col xs={24} sm={12} md={8} lg={6} key={customer.id}>
                    <Card
                      hoverable
                      style={{ 
                        borderRadius: '16px',
                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                        background: 'linear-gradient(135deg, #fff 0%, #f8f9fa 100%)',
                        border: 'none',
                        transition: 'all 0.3s ease'
                      }}
                      bodyStyle={{ padding: '6px' }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-4px) scale(1.02)';
                        e.currentTarget.style.boxShadow = '0 8px 30px rgba(0, 0, 0, 0.2)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0) scale(1)';
                        e.currentTarget.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.1)';
                      }}
                      actions={[
                        <Tooltip title="编辑" key="edit">
                          <Button
                            type="text"
                            icon={<EditOutlined />}
                            onClick={() => {
                              setEditingItem(customer);
                              setModalType('customer');
                              customerForm.setFieldsValue(customer);
                              setModalVisible(true);
                            }}
                            size="small"
                          />
                        </Tooltip>,
                        <Popconfirm
                          title="确定要删除这个客户吗？"
                          onConfirm={() => handleDeleteCustomer(customer.id)}
                          okText="确定"
                          cancelText="取消"
                          key="delete"
                        >
                          <Tooltip title="删除">
                            <Button
                              type="text"
                              danger
                              icon={<DeleteOutlined />}
                              size="small"
                            />
                          </Tooltip>
                        </Popconfirm>
                      ]}
                    >
                      <div style={{ textAlign: 'center' }}>
                        <Title level={5} style={{ margin: '8px 0', fontSize: '14px' }}>
                          {customer.name}
                        </Title>
                        <div style={{ marginBottom: '8px' }}>
                          <Text type="secondary" style={{ fontSize: '12px' }}>
                            📞 {customer.phone || '未设置'}
                          </Text>
                        </div>
                        <div style={{ marginBottom: '8px' }}>
                          <Text type="secondary" style={{ fontSize: '12px' }}>
                            📧 {customer.email || '未设置'}
                          </Text>
                        </div>
                        {customer.type_name && (
                          <div style={{ marginBottom: '8px' }}>
                            <Tag color="blue" style={{ fontSize: '10px' }}>
                              {customer.type_name}
                            </Tag>
                          </div>
                        )}
                        {customer.address && (
                          <Text type="secondary" style={{ fontSize: '10px', display: 'block', marginTop: '8px' }}>
                            📍 {customer.address}
                          </Text>
                        )}
                      </div>
                    </Card>
                  </Col>
                ))}
              </Row>
            )}
            
            {/* 分页组件 */}
            {pagination.total > 0 && (
              <div style={{ textAlign: 'center', marginTop: '24px' }}>
                <Pagination
                  current={pagination.current}
                  pageSize={pagination.pageSize}
                  total={pagination.total}
                  onChange={handlePageChange}
                  showSizeChanger
                  showQuickJumper
                  showTotal={(total, range) => 
                    `第 ${range[0]}-${range[1]} 条，共 ${total} 条`
                  }
                  pageSizeOptions={['10', '20', '50', '100']}
                />
              </div>
            )}
          </TabPane>

          <TabPane tab="客户类型管理" key="types">
            <div style={{ 
              marginBottom: 16, 
              display: 'flex', 
              justifyContent: 'flex-end',
              flexWrap: 'wrap'
            }}>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => {
                  setEditingItem(null);
                  setModalType('type');
                  typeForm.resetFields();
                  setModalVisible(true);
                }}
                size="small"
              >
                添加客户类型
              </Button>
            </div>
            {types.length === 0 ? (
              <Empty
                style={{ padding: '40px 0' }}
                description={<span style={{ color: '#8c8c8c' }}>暂无客户类型，点击“添加客户类型”开始管理客户分类</span>}
              />
            ) : (
              <Row gutter={[16, 16]}>
                {types.map((type) => (
                  <Col xs={24} sm={12} md={8} lg={6} key={type.id}>
                    <Card
                      hoverable
                      style={{ 
                        borderRadius: '16px',
                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                        background: 'linear-gradient(135deg, #fff 0%, #f8f9fa 100%)',
                        border: 'none',
                        transition: 'all 0.3s ease'
                      }}
                      bodyStyle={{ padding: '6px' }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-4px) scale(1.02)';
                        e.currentTarget.style.boxShadow = '0 8px 30px rgba(0, 0, 0, 0.2)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0) scale(1)';
                        e.currentTarget.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.1)';
                      }}
                      actions={[
                        <Button 
                          type="text" 
                          icon={<EditOutlined />} 
                          onClick={() => {
                            setEditingItem(type);
                            setModalType('type');
                            typeForm.setFieldsValue(type);
                            setModalVisible(true);
                          }}
                          size="small"
                          key="edit"
                        >
                          编辑
                        </Button>,
                        <Popconfirm
                          title="确定要删除这个客户类型吗？"
                          onConfirm={() => handleDeleteType(type.id)}
                          okText="确定"
                          cancelText="取消"
                          key="delete"
                        >
                          <Button 
                            type="text" 
                            danger 
                            icon={<DeleteOutlined />}
                            size="small"
                          >
                            删除
                          </Button>
                        </Popconfirm>
                      ]}
                    >
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '32px', marginBottom: '8px' }}>
                          🏷️
                        </div>
                        <Title level={5} style={{ margin: '8px 0', fontSize: '14px' }}>
                          {type.type_name}
                        </Title>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          {type.description || '无描述'}
                        </Text>
                      </div>
                    </Card>
                  </Col>
                ))}
              </Row>
            )}
          </TabPane>
        </Tabs>
      </Card>

      {/* 客户/类型编辑模态框 */}
      {modalVisible && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 999999,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '24px',
            borderRadius: '8px',
            width: '500px',
            maxHeight: '80vh',
            overflow: 'visible',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
          }}>
            <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', fontWeight: '600' }}>
              {modalType === 'customer' ? (editingItem ? '编辑客户' : '添加客户') : (editingItem ? '编辑客户类型' : '添加客户类型')}
            </h3>
        {modalType === 'customer' ? (
          <Form form={customerForm} onFinish={handleCustomerSubmit} layout="vertical">
            <Form.Item
              name="name"
              label="客户姓名"
              rules={[{ required: true, message: '请输入客户姓名' }]}
            >
              <Input prefix={<UserOutlined />} placeholder="请输入客户姓名" />
            </Form.Item>
            <Form.Item
              name="phone"
              label="电话号码"
              rules={[
                { pattern: /^1\d{10}$/, message: '请输入正确的11位手机号' }
              ]}
            >
              <Input prefix={<PhoneOutlined />} placeholder="请输入电话号码" />
            </Form.Item>
            <Form.Item
              name="type_id"
              label="客户类型"
            >
              <select 
                style={{ 
                  width: '100%', 
                  padding: '8px 12px', 
                  border: '1px solid #d9d9d9', 
                  borderRadius: '6px',
                  fontSize: '14px',
                  backgroundColor: 'white'
                }}
                value={customerForm.getFieldValue('type_id') || ''}
                onChange={(e) => customerForm.setFieldsValue({ type_id: e.target.value ? parseInt(e.target.value) : null })}
              >
                <option value="">请选择客户类型</option>
                {types.map(type => (
                  <option key={type.id} value={type.id}>
                    {type.type_name}
                  </option>
                ))}
              </select>
            </Form.Item>
            <Form.Item
              name="remark"
              label="备注"
            >
              <TextArea rows={3} placeholder="请输入备注信息" />
            </Form.Item>
          </Form>
        ) : (
          <Form form={typeForm} onFinish={handleTypeSubmit} layout="vertical">
            <Form.Item
              name="type_name"
              label="类型名称"
              rules={[{ required: true, message: '请输入类型名称' }]}
            >
              <Input prefix={<SettingOutlined />} placeholder="请输入类型名称" />
            </Form.Item>
            <Form.Item
              name="description"
              label="描述"
            >
              <TextArea rows={3} placeholder="请输入类型描述" />
            </Form.Item>
          </Form>
        )}
        
        <div style={{ textAlign: 'right', marginTop: '20px' }}>
          <button 
            onClick={() => {
              setModalVisible(false);
              setEditingItem(null);
              if (modalType === 'customer') {
                customerForm.resetFields();
              } else {
                typeForm.resetFields();
              }
            }}
            style={{ 
              marginRight: '10px', 
              padding: '8px 16px',
              border: '1px solid #d9d9d9',
              borderRadius: '4px',
              backgroundColor: 'white',
              cursor: 'pointer'
            }}
          >
            取消
          </button>
          <button 
            onClick={() => {
              if (modalType === 'customer') {
                handleCustomerSubmit(customerForm.getFieldsValue());
              } else {
                handleTypeSubmit(typeForm.getFieldsValue());
              }
            }}
            style={{ 
              padding: '8px 16px', 
              backgroundColor: '#1890ff', 
              color: 'white', 
              border: 'none', 
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            {editingItem ? '更新' : '添加'}
          </button>
        </div>
          </div>
        </div>
      )}

      {/* 跟进记录模态框 */}
      {followupModalVisible && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 999999,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '24px',
            borderRadius: '8px',
            width: '600px',
            maxHeight: '80vh',
            overflow: 'auto',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
          }}>
            <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', fontWeight: '600' }}>
              跟进记录 - {selectedCustomer?.name}
            </h3>
            
            <div style={{ marginBottom: 16 }}>
              <Form form={followupForm} onFinish={handleFollowupSubmit} layout="vertical">
                <Form.Item
                  name="followup_content"
                  label="跟进内容"
                  rules={[{ required: true, message: '请输入跟进内容' }]}
                >
                  <TextArea rows={4} placeholder="请输入跟进内容" />
                </Form.Item>
                <Form.Item>
                  <Button type="primary" htmlType="submit">
                    添加跟进记录
                  </Button>
                </Form.Item>
              </Form>
            </div>
            <Divider />
            <List
              dataSource={followups}
              renderItem={(item) => (
                <List.Item>
                  <div style={{ width: '100%' }}>
                    <div style={{ marginBottom: 8 }}>
                      <Text strong>{item.followup_content}</Text>
                    </div>
                    <div style={{ color: '#666', fontSize: '12px' }}>
                      {new Date(item.followup_date).toLocaleString('zh-CN')}
                    </div>
                  </div>
                </List.Item>
              )}
            />
            
            <div style={{ textAlign: 'right', marginTop: '20px' }}>
              <button 
                onClick={() => {
                  setFollowupModalVisible(false);
                  setSelectedCustomer(null);
                  setFollowups([]);
                }}
                style={{ 
                  padding: '8px 16px',
                  border: '1px solid #d9d9d9',
                  borderRadius: '4px',
                  backgroundColor: 'white',
                  cursor: 'pointer'
                }}
              >
                关闭
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Customers;
